import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import PartOne from './PartOne';
import CertificationSuccess from '../ComponentForms/CertificationSuccess';
import ErrorMessage from '../../../components/ErrorMessage';
import LoadingSpinner from '../../../components/LoadingSpinner';
import OrganisationLogo from '../../../components/OrganisationLogo';

// TYPES
import {
	AccountExistingCertificationsCertification,
	AccountJoinOrganisationCertification,
	AccountJoinOrganisationCommand,
	DomainCertificationType,
	DomainProfileType,
	OrganisationSearchOrganisationSearchSummary
} from '../../../api/models';

interface CertificationVisitorTypes {
	organisationData?: OrganisationSearchOrganisationSearchSummary;
	profileData?: { label?: string; value?: string } | undefined;
}

export interface VisitorCertificationTypes {
	purposeForVisit?: string;
	businessName?: string;
	existingWWCCRegistration?: AccountJoinOrganisationCertification[];
	workingWithChildren: AccountJoinOrganisationCertification;
	workingWithChildrenStatus?: string;
	acknowledge: boolean;
}

const CertificationVisitor: React.FC<CertificationVisitorTypes> = ({ organisationData, profileData }) => {
	const [formData, setFormData] = React.useState<VisitorCertificationTypes>({
		purposeForVisit: '',
		businessName: '',
		existingWWCCRegistration: [
			{
				certificationType: DomainCertificationType.WorkingWithChildrenCheck,
				state: '',
				expiry: '',
				certificationNumber: '',
				createAttachments: [],
				userAcknowledgement: false
			}
		],
		workingWithChildren: {
			certificationType: DomainCertificationType.WorkingWithChildrenCheck,
			state: '',
			expiry: '',
			certificationNumber: '',
			createAttachments: [],
			userAcknowledgement: false
		},
		acknowledge: false
	});
	const [formSection, setFormSection] = React.useState<string>('partOne');

	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [existingCertification, setExistingCertification] = React.useState<
		AccountExistingCertificationsCertification[]
	>([]);

	const getExistingCertifications = async () => {
		setIsLoading(true);

		const response = await apibridge.getAccountExistingCertifications();

		if (response.isError) {
			setIsLoading(false);
		} else {
			setExistingCertification(
				response.result &&
					response.result.filter(
						(item: AccountExistingCertificationsCertification) =>
							item.certificationType === DomainCertificationType.WorkingWithChildrenCheck && item.validCertification
					)
			);

			setIsLoading(false);

			setShowError(false);
		}
	};

	React.useEffect(() => {
		getExistingCertifications();
	}, []);

	// Submit form data to API

	const doJoin = async () => {
		const data: AccountJoinOrganisationCommand = {
			organisationIdentifier: organisationData?.identifier,
			profileType: profileData?.value as DomainProfileType,
			visitPurpose: formData?.purposeForVisit,
			businessName: formData?.businessName,
			volunteerRequest: true,
			certifications: [{ ...formData.workingWithChildren }] || []
		};

		setIsLoading(true);

		const response = await apibridge.doJoinOrganisation(data);

		if (response.isError) {
			setIsLoading(false);

			setShowError(true);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setFormSection('success');
		}
	};

	return (
		<div className="page-add-certification">
			{showError ? (
				<ErrorMessage message={formErrorMessage} />
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="header row">
									<div className="section-header offset-col-2 col-8">
										{formSection === 'success' ? null : (
											<>
												<OrganisationLogo src={organisationData && organisationData.image} />

												<h2 className="h1">Registering with {organisationData?.name} as a Site Visitor</h2>

												<p className="sub-grey-text">Please provide additional certification information.</p>
											</>
										)}
									</div>
								</div>

								{formSection === 'partOne' ? (
									<PartOne
										setFormSection={setFormSection}
										setFormData={setFormData}
										formData={formData}
										doJoin={doJoin}
										loading={isLoading}
										existingCertification={existingCertification}
									/>
								) : formSection === 'success' ? (
									<CertificationSuccess organisationName={organisationData && organisationData.name} />
								) : null}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default CertificationVisitor;
